import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import debounce from "lodash/debounce";
import {
  Table,
  Checkbox,
  message,
  Card,
  Row,
  Col,
  Input,
  Button,
  Spin,
} from "antd";
import { API_URL } from "../../url_config";
import { NavigationStepsCapJour } from "../../components/navbars/navbar_cap_jour";
import { EditOutlined, ReloadOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTable from "../../components/counter";

const { TextArea } = Input;

// === Styles tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }

  .comment-cell textarea {
    width: 100%;
    resize: none;
    border-radius: 6px;
  }
`;
document.head.appendChild(tableStyle);

// === Composant de cellule fluide ===
const EditableTextCell = ({ recordId, initialValue, field, onPatch }) => {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  const debouncedSave = useCallback(
    debounce((val) => {
      onPatch(recordId, field, val);
    }, 800),
    [recordId, field, onPatch]
  );

  const handleChange = (e) => {
    const val = e.target.value;
    setValue(val);
    debouncedSave(val);
  };

  return (
    <TextArea
      rows={2}
      value={value}
      onChange={handleChange}
      placeholder="Saisissez un commentaire..."
      autoSize
    />
  );
};

// === Ligne développée (comment + aide) ===
function TableCoquilleExpandable({ record, onPatch }) {
  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <EditableTextCell
              recordId={record.id}
              initialValue={record.comment}
              field="comment"
              onPatch={onPatch}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Informations (Supervision)" bordered>
            <EditableTextCell
              recordId={record.id}
              initialValue={record.aide}
              field="aide"
              onPatch={onPatch}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationCoquilleConfigDspCapJour = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/validations/validation_coquille_config_dsp/?ot_tag=cap_jour`
      );
      const formatted = res.data.map((item, index) => ({
        key: item.id || index,
        id: item.id,
        dsp: item.dsp,
        coquille: item.coquille,
        validation_sortie_coquille: item.validation_sortie_coquille,
        commentaire: item.commentaire,
        comment: item.comment,
        aide: item.aide,
        signal: item.signal,
        ot_tag: item.ot_tag,
      }));
      setData(formatted);
    } catch (error) {
      console.error(error);
      message.error("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // === PATCH fluide (non bloquant) ===
  const handlePatch = useCallback(async (id, field, value) => {
    try {
      await axios.patch(
        `${API_URL}/validations/validation_coquille_config_dsp/${id}/`,
        { [field]: value }
      );
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    }
  }, []);

  // === Mise à jour locale immédiate + PATCH asynchrone ===
  const handleLocalChange = (key, field, value, recordId) => {
    setData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );

    if (recordId) {
      handlePatch(recordId, field, value);
    }
  };

  const columns = [
    { title: "DSP", dataIndex: "dsp" },
    { title: "Coquille", dataIndex: "coquille" },
    {
      title: "Validation sortie coquille",
      dataIndex: "validation_sortie_coquille",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleLocalChange(
              record.key,
              "validation_sortie_coquille",
              e.target.checked,
              record.id
            )
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Commentaire",
      dataIndex: "commentaire",
      className: "comment-cell",
      render: (val, record) => (
        <EditableTextCell
          recordId={record.id}
          initialValue={val}
          field="commentaire"
          onPatch={handlePatch}
        />
      ),
    },
    {
      title: "État",
      dataIndex: "signal",
      render: (val, record) => {
        const color =
          val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
        const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

        const handleClick = () => {
          const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
          handleLocalChange(record.key, "signal", nextVal, record.id);
        };

        return (
          <div
            onClick={handleClick}
            style={{
              cursor: "pointer",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                backgroundColor: color,
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "16px",
                userSelect: "none",
                transition: "all 0.3s ease",
              }}
            >
              {symbol}
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px", position: "relative" }}>
      {/* Spinner full screen */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255,255,255,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Spin size="large" tip="Chargement..." />
        </div>
      )}

      <NavigationStepsCapJour selectedKey="coquille_config_dsp" />

        <CoquillesTable url={`${API_URL}/validations/counter/?ot_tag=cap_jour`} />


      {/* Bouton fixe */}
      <Button
        type="primary"
        icon={<ReloadOutlined />}
        onClick={fetchData}
        style={{
          position: "fixed",
          top: 20,
          right: 20,
          zIndex: 10000,
        }}
      >
        Rafraîchir
      </Button>

      <Table
        columns={columns}
        dataSource={data}
        loading={false} // désactivé car on gère loading global
        expandable={{
          expandedRowRender: (record) => (
            <TableCoquilleExpandable record={record} onPatch={handlePatch} />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          marginTop: 60, // espace pour le bouton fixe
        }}
        rowClassName={() => "custom-row"}
        pagination={false}
      />

      <div style={{ padding: 20 }}>
        <ModifyItemPage
          apiUrl={`${API_URL}/validations/validation_coquille_config_dsp/`}
          ot_tag="cap_jour"
          title="Gestion"
          rowKey="id"
          trigger={<Button icon={<EditOutlined />} shape="circle" />}
          columns={[
            { title: "Dsp", dataIndex: "dsp", key: "dsp" },
            { title: "Coquille", dataIndex: "coquille", key: "coquille" },
          ]}
          formFields={[
            { name: "dsp", label: "Dsp", placeholder: "Entrez la Dsp" },
            { name: "coquille", label: "Coquille", placeholder: "Entrez la coquille" },
          ]}
        />
      </div>
    </div>
  );
};

export default ValidationCoquilleConfigDspCapJour;
